import { useState, useMemo } from 'react';
import { Barcode } from 'lucide-react';

// "D35 MM ESQUERDA CURTA"  → { primary: "D35", modifier: "ESQUERDA CURTA" }
// "Ø400 MM H15"            → { primary: "Ø400", modifier: "H15" }
// "L30 (C45)"              → { primary: "L30",  modifier: "(C45)" }
// "GR-100"                 → { primary: "GR-100", modifier: "" }
// "POS.2"                  → { primary: "POS.2", modifier: "" }
// "BK-120 M14"             → { primary: "BK-120", modifier: "M14" }
export function parseVariantLabel(label = '') {
  const s = String(label ?? '').trim();

  let m = s.match(/^([DØ]\d+(?:[,.]\d+)?)(?:\s*MM)?\s*(.*)/i);
  if (m) return { primary: m[1].toUpperCase(), modifier: m[2].trim() };

  m = s.match(/^(L\d+)\s*(.*)/i);
  if (m) return { primary: m[1].toUpperCase(), modifier: m[2].trim() };

  m = s.match(/^(GR[-.]?\d+)\s*(.*)/i);
  if (m) return { primary: m[1].toUpperCase(), modifier: m[2].trim() };

  m = s.match(/^(POS\.\d+)\s*(.*)/i);
  if (m) return { primary: m[1].toUpperCase(), modifier: m[2].trim() };

  m = s.match(/^([A-Z]{2,3}-\d+)\s*(.*)/i);
  if (m) return { primary: m[1].toUpperCase(), modifier: m[2].trim() };

  return { primary: s, modifier: '' };
}

function formatModifier(raw, groupMods = []) {
  const hasM14 = groupMods.some(m => /M14/i.test(m));
  const hasFS  = groupMods.some(m => /c\/FS|s\/FS/i.test(m));
  // Empty or "Standard" label → derive label from context
  if (!raw || /^standard$/i.test(raw.trim())) {
    if (hasM14) return 'Sem M14';
    if (hasFS)  return 'Sem Force System';
    return raw ? raw : 'Direita';
  }
  // If modifier contains M14, check what's left after stripping noise
  // e.g. "M14" → "M14", "TURBO PERFORADO M14" → "M14"
  if (/\bM14\b/i.test(raw) && hasM14) {
    const withoutM14 = raw.replace(/TURBO PERFORADO\s*/gi, '').replace(/\bM14\b/gi, '').trim();
    if (!withoutM14) return 'M14';
  }
  const result = raw
    .replace(/\(C45\)/gi, 'C45')
    .replace(/TURBO PERFORADO\s*/gi, '')
    .replace(/\bDIREITA\b/gi, 'Dir.')
    .replace(/\bESQUERDA\b/gi, 'Esq.')
    .replace(/\bCURTA\b/gi, 'Curta')
    .replace(/^DX\s+/i, '')
    .replace(/\bc\/FS\b/gi, 'c/ Force System')
    .replace(/\bs\/FS\b/gi, 's/ Force System')
    .replace(/\bM14\b/gi, '')
    .trim();
  // After stripping, if empty check context
  if (!result) {
    if (hasM14) return 'Sem M14';
    if (hasFS)  return 'Sem Force System';
    return 'Direita';
  }
  return result;
}

function autoPrimaryLabel(primaries) {
  if (primaries.every(p => /^[DØ]\d/.test(p))) return 'Diâmetro';
  if (primaries.every(p => /^L\d/.test(p))) return 'Comprimento';
  if (primaries.every(p => /^GR/.test(p))) return 'Grão';
  if (primaries.every(p => /^POS/.test(p))) return 'Posição';
  return 'Tamanho';
}

function autoModifierLabel(modifiers) {
  const flat = modifiers.join(' ').toUpperCase();
  if (/C\/FS|S\/FS/.test(flat)) return 'Force System';
  if (/INOX|C45/.test(flat)) return 'Material';
  if (/H\d{2}/.test(flat)) return 'Altura';
  if (/M14/.test(flat)) return 'Encaixe';
  if (/ESQ/.test(flat)) return 'Direção';
  return 'Opção';
}

// 'Esquerda' if modifier contains ESQUERDA, else 'Direita'
function vDir(modifier) {
  return /ESQUERDA/i.test(modifier) ? 'Esquerda' : 'Direita';
}

// 'Curta' if modifier contains CURTA, else 'Normal'
function vLen(modifier) {
  return /CURTA/i.test(modifier) ? 'Curta' : 'Normal';
}

// True if the set of variants spans both Esquerda and Direita
function hasDirectionDim(variants) {
  return variants.some(v => /ESQUERDA/i.test(v.modifier))
      && variants.some(v => !/ESQUERDA/i.test(v.modifier));
}

// True if the set of variants spans both Curta and Normal
function hasLengthDim(variants) {
  return variants.some(v => /CURTA/i.test(v.modifier))
      && variants.some(v => !/CURTA/i.test(v.modifier));
}

const chipBase = 'px-3 py-1.5 rounded-xl border text-sm font-medium transition-all select-none';
const chipActive = 'bg-blue-600 border-blue-500 text-white shadow-lg shadow-blue-500/20';
const chipNormal = 'border-slate-700 text-slate-300 hover:border-blue-500/50 hover:text-white';
const chipDisabled = 'border-slate-800 text-slate-600 cursor-not-allowed';

export const SmartVariantPicker = ({ variants, selectedVariant, onSelect, onBarcode, compact = false, ignoreStock = false }) => {
  const isOut = (v) => !ignoreStock && (v.stock ?? 0) <= 0;
  const parsed = useMemo(() =>
    (variants || []).filter(Boolean).map(v => ({ ...v, ...parseVariantLabel(v.label) })),
    [variants]
  );

  const groups = useMemo(() => {
    const g = {};
    parsed.forEach(v => {
      if (!g[v.primary]) g[v.primary] = [];
      g[v.primary].push(v);
    });
    return g;
  }, [parsed]);

  const primaries = Object.keys(groups).sort((a, b) => {
    // Extract the trailing integer (e.g. "GR.50" → 50, "GR.1500" → 1500)
    const numA = a.match(/(\d+)\s*$/);
    const numB = b.match(/(\d+)\s*$/);
    const na = numA ? parseInt(numA[1], 10) : Infinity; // no number → goes last
    const nb = numB ? parseInt(numB[1], 10) : Infinity;
    return na !== nb ? na - nb : a.localeCompare(b);
  });
  const hasGroups = primaries.length > 1;

  const variantPrimary = selectedVariant ? parseVariantLabel(selectedVariant.label).primary : null;
  const [localPrimary, setLocalPrimary] = useState(variantPrimary);
  const activePrimary = variantPrimary || localPrimary;

  const [localDirection, setLocalDirection] = useState(
    selectedVariant ? vDir(parseVariantLabel(selectedVariant.label).modifier) : null
  );

  const handlePrimary = (p) => {
    if (activePrimary === p) {
      onSelect(null);
      setLocalPrimary(null);
      setLocalDirection(null);
    } else {
      setLocalPrimary(p);
      setLocalDirection(null);
      onSelect(null);
      const group = groups[p];
      if (group.length === 1) onSelect(group[0]);
    }
  };

  // When every variant has a `color` field → color swatch picker (ignores grouping logic)
  const allHaveColor = (variants || []).filter(Boolean).every(v => v.color);

  // Stable key for variants that may lack an `id` field
  const variantKey = v => v.id ?? v.reference ?? v.label;
  const selKey = selectedVariant ? variantKey(selectedVariant) : null;

  if (allHaveColor) {
    return (
      <div>
        <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-2 font-medium">Cor</p>
        <div className="flex flex-wrap gap-3">
          {(variants || []).filter(Boolean).map(v => {
            const active = selKey !== null && selKey !== undefined && selKey === variantKey(v);
            const soldOut = isOut(v);
            const isLight = v.color && /^#(d|c|e|f)/i.test(v.color);
            return (
              <button
                key={variantKey(v)}
                type="button"
                onClick={() => onSelect(active ? null : v)}
                disabled={soldOut}
                title={v.label}
                className="relative flex flex-col items-center gap-1.5 group/swatch"
              >
                <span
                  className="w-9 h-9 rounded-full transition-all"
                  style={{
                    backgroundColor: v.color,
                    border: active
                      ? '2.5px solid white'
                      : isLight
                        ? '1.5px solid rgba(255,255,255,0.25)'
                        : '1.5px solid rgba(255,255,255,0.08)',
                    boxShadow: active
                      ? `0 0 0 2.5px ${v.color}, 0 0 0 5px rgba(255,255,255,0.12)`
                      : soldOut
                        ? 'none'
                        : `0 2px 10px ${v.color}66`,
                    opacity: soldOut ? 0.3 : 1,
                  }}
                />
                <span className={`text-[10px] font-medium leading-none transition-colors ${
                  active ? 'text-slate-100' : soldOut ? 'text-slate-600' : 'text-slate-500 group-hover/swatch:text-slate-300'
                }`}>
                  {v.label}
                </span>
                {soldOut && (
                  <span className="absolute top-0 right-0 w-3.5 h-3.5 rounded-full bg-slate-800 border border-slate-600 flex items-center justify-center">
                    <span className="text-[7px] text-slate-400">×</span>
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // ── Flat text chips (variants without color field) ──
  if (!hasGroups) {
    return (
      <div className="flex flex-wrap gap-2">
        {(variants || []).filter(Boolean).map(v => {
          const active = selectedVariant?.id === v.id;
          const soldOut = isOut(v);
          return (
            <div key={v.id} className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => onSelect(active ? null : v)}
                disabled={soldOut}
                className={`${chipBase} ${soldOut ? chipDisabled + ' line-through' : active ? chipActive : chipNormal}`}
              >
                {v.label}
                {soldOut && <span className="ml-1 text-xs">(esgotado)</span>}
                {!soldOut && (v.stock ?? 0) > 0 && v.stock <= 5 && <span className="ml-1 text-xs text-amber-400">·{v.stock}</span>}
              </button>
              {onBarcode && (v.sku || v.reference) && (
                <button type="button" onClick={() => onBarcode(v)}
                  className="w-7 h-7 flex items-center justify-center rounded-lg border border-slate-700 text-slate-500 hover:text-blue-400 hover:border-blue-500/50 transition-colors">
                  <Barcode className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    );
  }

  // ── Smart multi-level picker ───────────────────────────────────────────────
  const subGroup = (activePrimary ? groups[activePrimary] : null) ?? [];
  const rawMods = subGroup.map(v => v.modifier);

  // 3-level: variants span both direction AND length dimensions
  const isThreeLevel = subGroup.length > 0
    && hasDirectionDim(subGroup)
    && hasLengthDim(subGroup);

  // Active direction: from currently selected variant, or local state
  const selectedDir = selectedVariant && activePrimary
    ? vDir(parseVariantLabel(selectedVariant.label).modifier)
    : null;
  const activeDirection = selectedDir || localDirection;

  // Length-level variants filtered by active direction
  const lenVariants = activeDirection
    ? subGroup.filter(v => vDir(v.modifier) === activeDirection)
    : [];

  // Direction chips present in current subGroup
  const presentDirs = ['Direita', 'Esquerda'].filter(dir =>
    subGroup.some(v => vDir(v.modifier) === dir)
  );

  return (
    <div className="space-y-3">

      {/* Row 1 — primary dimension (diameter, size, etc.) */}
      <div>
        <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-2 font-medium">
          {autoPrimaryLabel(primaries)}
        </p>
        <div className="flex flex-wrap gap-1.5">
          {primaries.map(p => {
            const isActive = activePrimary === p;
            const allOut = groups[p].every(isOut);
            return (
              <button type="button" key={p} onClick={() => handlePrimary(p)} disabled={allOut}
                className={`${chipBase} ${allOut ? chipDisabled : isActive ? chipActive : chipNormal}`}
              >
                {p}
                {allOut && <span className="ml-1 text-xs opacity-60">(esgt.)</span>}
              </button>
            );
          })}
        </div>
      </div>

      {/* 3-level Row 2 — direction (Direita / Esquerda) */}
      {activePrimary && isThreeLevel && (
        <div>
          <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-2 font-medium">
            DIREÇÃO
          </p>
          <div className="flex flex-wrap gap-1.5">
            {presentDirs.map(dir => {
              const isActive = activeDirection === dir;
              const dirVars = subGroup.filter(v => vDir(v.modifier) === dir);
              const allOut = dirVars.every(isOut);
              return (
                <button type="button" key={dir}
                  onClick={() => {
                    if (isActive) { setLocalDirection(null); onSelect(null); }
                    else {
                      setLocalDirection(dir);
                      if (dirVars.length === 1) onSelect(dirVars[0]);
                      else onSelect(null);
                    }
                  }}
                  disabled={allOut}
                  className={`${chipBase} ${allOut ? chipDisabled : isActive ? chipActive : chipNormal}`}
                >
                  {dir}
                  {allOut && <span className="ml-1 text-xs opacity-60">(esgt.)</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* 3-level Row 3 — length (Normal / Curta) */}
      {activePrimary && isThreeLevel && activeDirection && lenVariants.length > 0 && (
        <div>
          <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-2 font-medium">
            COMPRIMENTO
          </p>
          <div className="flex flex-wrap gap-1.5">
            {lenVariants.map(v => {
              const active = selectedVariant?.id === v.id;
              const soldOut = isOut(v);
              return (
                <div key={v.id} className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => onSelect(active ? null : v)}
                    disabled={soldOut}
                    className={`${chipBase} ${soldOut ? chipDisabled + ' line-through' : active ? chipActive : chipNormal}`}
                  >
                    {vLen(v.modifier)}
                    {soldOut && <span className="ml-1 text-xs">(esgotado)</span>}
                    {!soldOut && (v.stock ?? 0) > 0 && v.stock <= 5 && <span className="ml-1 text-xs text-amber-400">·{v.stock}</span>}
                    {!compact && v.price_override != null && (
                      <span className="ml-1.5 text-slate-400 text-xs">{v.price_override}€</span>
                    )}
                  </button>
                  {onBarcode && (v.sku || v.reference) && (
                    <button type="button" onClick={() => onBarcode(v)}
                      className="w-7 h-7 flex items-center justify-center rounded-lg border border-slate-700 text-slate-500 hover:text-blue-400 hover:border-blue-500/50 transition-colors">
                      <Barcode className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* 2-level modifier row — only shown when there are multiple options to choose from */}
      {activePrimary && !isThreeLevel && subGroup.length > 1 && (
        <div>
          <p className="text-[10px] text-slate-500 uppercase tracking-wider mb-2 font-medium">
            {autoModifierLabel(rawMods)}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {subGroup.map(v => {
              const active = selectedVariant?.id === v.id;
              const soldOut = isOut(v);
              const modLabel = formatModifier(v.modifier, rawMods);
              const isSingle = subGroup.length === 1;
              return (
                <div key={v.id} className="flex items-center gap-1">
                  <button
                    type="button"
                    onClick={() => { if (!isSingle) onSelect(active ? null : v); }}
                    disabled={soldOut}
                    className={`${chipBase} ${soldOut ? chipDisabled + ' line-through' : active ? chipActive : chipNormal} ${isSingle ? 'cursor-default' : ''}`}
                  >
                    {modLabel}
                    {soldOut && <span className="ml-1 text-xs">(esgotado)</span>}
                    {!soldOut && (v.stock ?? 0) > 0 && v.stock <= 5 && <span className="ml-1 text-xs text-amber-400">·{v.stock}</span>}
                    {!compact && v.price_override != null && !active && (
                      <span className="ml-1.5 text-slate-400 text-xs">{v.price_override}€</span>
                    )}
                  </button>
                  {onBarcode && (v.sku || v.reference) && (
                    <button type="button" onClick={() => onBarcode(v)}
                      className="w-7 h-7 flex items-center justify-center rounded-lg border border-slate-700 text-slate-500 hover:text-blue-400 hover:border-blue-500/50 transition-colors">
                      <Barcode className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

    </div>
  );
};
